import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatLink from "src/utils/formatLink";
import IModalCommentsOrLinkRef, { IModalCommentsOrLink, IParamsCommentsOrLink } from "./interface";

const ModalCommentsOrLink: React.ForwardRefRenderFunction<IModalCommentsOrLinkRef, IModalCommentsOrLink> = ({
	onCallback
}, ref) => {
	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();

	const modalCommentOrLink = useRef<RCCRefModalProps>(null);
	const memorizedParamsComments = useRef<IParamsCommentsOrLink>({} as IParamsCommentsOrLink);
	const [permissions, setPermissions] = useState(false);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const [comment, setComment] = useState("");
	const [link, setLink] = useState("");
	const [isLink, setIsLink] = useState(false);

	const { 
		handleSubmit, 
		register, 
		reset
	} = useForm();

	const openModalComments = async (paramsComments: IParamsCommentsOrLink) => {
		reset();
		setComment("");
		setIsLink(false);
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectCommentOrLinkSpreadsheet",
				urlParameters: {
					...paramsComments,
					returnLink: false
				}
			});

			memorizedParamsComments.current = paramsComments;

			setComment(data.commentOrLink);
			setPermissions(data.canEdit);
			setSubtitle(paramsComments.date.substring(3));

			setLoading(false);
			modalCommentOrLink.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar comentário"), addToast);
		}
	};

	const openModalLink = async (paramsComments: IParamsCommentsOrLink) => {
		reset();
		setComment("");
		setIsLink(true);
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectCommentOrLinkSpreadsheet",
				urlParameters: {
					...paramsComments,
					returnLink: true
				}
			});

			memorizedParamsComments.current = paramsComments;

			setComment(data.commentOrLink);
			setLink(data);
			setPermissions(data.canEdit);
			setSubtitle(paramsComments.date.substring(3));

			setLoading(false);
			modalCommentOrLink.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar link"), addToast);
		}
	};

	const submit = async (data: any) => {
		try{
			modalCommentOrLink.current?.setLoadingButton();

			await RCCServicesApi.postRequest({
				method: "FinancialManagement/UpdateCommentOrLinkSpreadsheet",
				body: {
					comment: data.comment,
					...memorizedParamsComments.current,
					isLink
				}
			});

			addToast({
				color: "green",
				description: isLink ? translate("Link adicionado com sucesso") : translate("Comentário adicionado com sucesso"),
				title: translate("Sucesso")
			});

			modalCommentOrLink.current?.resetLoadingButton();
			modalCommentOrLink.current?.closeModal();
    
			onCallback();

		}catch(err){
			modalCommentOrLink.current?.resetLoadingButton();
			standardErrorMessages(err, isLink ? translate("Erro ao adicionar link") : translate("Erro ao adicionar comentário"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalComments,
		openModalLink
	}));

	return (
		<>
			
			<Modal
				ref={modalCommentOrLink}
				header={{
					title: isLink ? "Link" : translate("Comentário"),
					subtitle,
					icon: {
						name: isLink ? "linkExternal" : "comment"
					}
				}}
				footer= {{
					showSaveButton: permissions,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				onSubmit={handleSubmit(submit)}
				styleInline={`${ModalSide} height: 30%; min-height: 300px;`}
			>
				<RCCGrid container item direction="column">
					<RCCTextArea 
						register={register}
						name="comment"
						label=""
						maxLength={isLink ? 1000 : 2000}
						defaultValue={comment}
						styleInline="height: 34px;"
						disabled={!permissions}
						onBlurCapture={isLink ? (e: any) => setLink(e.target.value) : undefined}
					/>
				</RCCGrid>
				{isLink && link.length > 0 && (
					<RCCGrid container item justifyContent="flex-end">
						<RCCButton
							layout="isRounded"
							icon={{
								name: "linkExternal"
							}}
							color="purple"
							onClick={() => window.open(formatLink(link), "_blank")}
						/>
					</RCCGrid>
				)}
			</Modal>
			
		</>
	);
};

export default forwardRef(ModalCommentsOrLink);
